// Reusable client-side table sorting — a hook + a clickable header cell, used by
// every data table in the tenant app. Sorting is purely client-side over the
// rows already loaded into the table (no API round-trip): clicking a header
// cycles unsorted → ascending → descending → unsorted.
//
// Usage:
//   const ACCESSORS = { name: (r) => r.name, due: (r) => r.due_date };
//   const { rows, sort, toggle } = useTableSort(projects, ACCESSORS);
//   <th> → <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
//   then map over `rows` instead of the raw array.
//
// Define ACCESSORS as a module-level constant (stable identity) so the memo
// doesn't re-sort every render. Only list the fields you make sortable.

import { useMemo, useState, type ReactNode } from "react";

export type SortDir = "asc" | "desc";
export interface SortState {
  field: string;
  dir: SortDir;
}

/** Pulls the comparable value for a row from the active field. */
export type SortAccessor<T> = (row: T) => string | number | boolean | null | undefined;

/** Base comparison for two non-null values: numeric-aware string compare, real
 *  numeric compare for numbers, false<true for booleans. */
function baseCompare(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a ? 1 : 0) - (b ? 1 : 0);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Client-side sort over `rows`. `accessors` maps a field key (matching the
 * `field` on each {@link SortTh}) to the value to sort by. Empty/null values
 * always sort last, regardless of direction. Returns the sorted rows (the
 * original array when unsorted), the current sort, and a `toggle(field)` that
 * cycles asc → desc → unsorted.
 */
export function useTableSort<T>(
  rows: T[],
  accessors: Record<string, SortAccessor<T>>,
  initial?: SortState,
): { rows: T[]; sort: SortState | null; toggle: (field: string) => void } {
  const [sort, setSort] = useState<SortState | null>(initial ?? null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const get = accessors[sort.field];
    if (!get) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      const aEmpty = av == null || av === "";
      const bEmpty = bv == null || bv === "";
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1; // empties last, independent of direction
      if (bEmpty) return -1;
      return dir * baseCompare(av, bv);
    });
  }, [rows, sort, accessors]);

  function toggle(field: string): void {
    setSort((s) => {
      if (!s || s.field !== field) return { field, dir: "asc" };
      return s.dir === "asc" ? { field, dir: "desc" } : null;
    });
  }

  return { rows: sorted, sort, toggle };
}

/** A sortable `<th>`: renders the label as a button that cycles the sort for
 *  `field`, with a direction caret and `aria-sort` for accessibility. Drop-in
 *  replacement for a plain `<th>` in any table wired to {@link useTableSort}. */
export function SortTh({
  field,
  sort,
  onSort,
  children,
  className,
}: {
  field: string;
  sort: SortState | null;
  onSort: (field: string) => void;
  children: ReactNode;
  className?: string;
}) {
  const active = sort?.field === field;
  const dir = active ? sort!.dir : null;
  return (
    <th className={className} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`th-sort${active ? " active" : ""}`} onClick={() => onSort(field)}>
        <span>{children}</span>
        <span className="th-sort-caret" aria-hidden="true">
          {active ? (dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}
